import { useState, useEffect } from 'react';

/**
 * Custom hook for managing toggle states with localStorage persistence
 * @param key - The localStorage key to store the toggle state
 * @param defaultValue - The default value if no stored value exists
 * @returns [value, setValue] - Current value and setter function
 */
export function useLocalStorageToggle(key: string, defaultValue: boolean = false): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      // Try to get the value from localStorage
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setStoredValue = (newValue: boolean) => {
    try {
      // Update state
      setValue(newValue);
      // Update localStorage
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [value, setStoredValue];
}

/**
 * Hook specifically for spam filter toggle state
 */
export function useSpamFilterToggle(): [boolean, (value: boolean) => void] {
  return useLocalStorageToggle('analytics_spam_filter_enabled', false);
}

/**
 * Hook specifically for CTOR toggle state
 */
export function useCTORToggle(): [boolean, (value: boolean) => void] {
  return useLocalStorageToggle('analytics_ctor_enabled', false);
}

/**
 * Hook specifically for duplicate interactions filter toggle state
 */
export function useDuplicateInteractionsToggle(): [boolean, (value: boolean) => void] {
  return useLocalStorageToggle('analytics_duplicate_interactions_filter_enabled', false);
}
